<script setup lang="ts">
  import { IconAccount } from '@gat-ship/icons'
  import type { TenantUser } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const description = ref('')

  const changeJobTitleSchema = z.object({
    userId: z.string().min(1),
    jobTitle: z.string().optional()
  })
  type ChangeJobTitleFormValues = z.infer<typeof changeJobTitleSchema>

  const form = useForm({
    defaultValues: {
      userId: '',
      jobTitle: undefined
    } as ChangeJobTitleFormValues,
    validators: {
      onChange: changeJobTitleSchema
    },
    onSubmit: async ({ value }) => {
      await tenantUserProvider.commands.changeJobTitle({
        userId: value.userId,
        jobTitle: value.jobTitle
      })
      isOpen.value = false
    }
  })

  export interface ShowChangeJobTitleModalParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowChangeJobTitleModalParams) {
    description.value = tenantUser.fullName
    form.reset({
      userId: tenantUser.userId,
      jobTitle: tenantUser.jobTitle
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAccount class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Change Job Title
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="jobTitle" label="Job Title">
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="
                  testIds.tenantUser.forms.changeJobTitle.titleInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
